<template>
  <v-dialog
    v-model="dialog"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :hide-overlay="$vuetify.breakpoint.xsOnly"
    :max-width="$vuetify.breakpoint.smAndUp ? 400 : null"
    transition="dialog-bottom-transition"
    persistent
  >
    <v-card>
      <v-overlay
        :value="volumeTokenLoading || paymentLoading || stripeLoading || checkingDone"
        :dark="false"
        color="white"
        opacity="0.90"
        :absolute="true"
      >
        <div class="text-center pa-5">
          <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
          <div>{{ $t('message.request_being_processed') }}</div>
        </div>
      </v-overlay>
      <v-toolbar dark flat color="primary" class="px-2">
        <v-toolbar-title>{{ $t('message.add_new_volume_token') }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit">
        <v-card-text class="mt-6">
          <template v-if="servicePeriod.airtimePlan.allowedTokens.length > 1">
            <div class="v-label">{{ $t('message.choose_a_volume') }}</div>
            <v-radio-group v-model="selectedVolume">
              <v-radio
                v-for="allowedToken in servicePeriod.airtimePlan.allowedTokens"
                :key="allowedToken.volume"
                :label="allowedToken.volume + ' GB'"
                :value="allowedToken"
              ></v-radio>
            </v-radio-group>
          </template>

          <v-alert
            :value="true"
            class="grey lighten-4"
            border="left"
            colored-border
            type="info"
            v-html="
              $t('message.selected_volume_token_info', {
                volume: volumeToken.volume,
                airtimePlanName: servicePeriod.airtimePlan.name
              })
            "
          >
          </v-alert>

          <div class="mb-6">
            <div class="headline">
              {{ $n(selectedVolume.price, servicePeriod.agreement.currency.code) }}
            </div>
            <div class="caption grey--text">
              {{ $n(grossPrice, servicePeriod.agreement.currency.code) }}
              ({{
                $t('message.tax_incl', {
                  tax: servicePeriod.agreement.legalEntity.vat
                })
              }})
            </div>
          </div>

          <div
            class="grey lighten-5 pa-4 mb-3"
            :class="{
              complete: cardComplete,
              'card-error': cardError,
              focus: cardFocus
            }"
          >
            <span class="font-weight-medium text-uppercase">{{ $t('message.card_details') }}</span>

            <v-text-field
              v-model="cardHolderName"
              :label="$t('message.card_holder_name')"
              required
              :rules="cardHolderNameRules"
            ></v-text-field>
            <CardElement
              ref="cardElement"
              :data="stripeData"
              @change="cardComplete = $event.complete && cardHolderName"
              @focus="cardFocus = true"
              @blur="cardFocus = false"
              @success="cardPaymentSuccess"
              @error="cardPaymentError"
              @loading="setStripeLoading"
            ></CardElement>
          </div>
          <span v-if="cardError" class="red--text">{{ cardError }}</span>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="!cardComplete" color="primary" text type="submit">
            {{ $t('message.pay') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import fetch from '@/utils/fetch'
import CardElement from '@/components/CardElement'

export default {
  name: 'VolumeTokenCreateDialog',
  components: { CardElement },
  props: {
    servicePeriod: {
      type: Object,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      valid: true,
      selectedVolume: this.servicePeriod.airtimePlan.allowedTokens[0],
      volumeToken: {
        servicePeriod: this.servicePeriod['@id'],
        volume: this.servicePeriod.airtimePlan.allowedTokens[0].volume
      },
      cardComplete: false,
      cardError: null,
      cardFocus: false,
      paymentMethod: 'credit_card_online',
      stripeLoading: false,
      cardHolderName: null,
      cardHolderNameRules: [
        v => {
          return (!!v && !!v.trim()) || this.$t('message.card_holder_name_is_required')
        }
      ],
      paymentDoneCheckInternval: null,
      checkingDone: false
    }
  },
  computed: {
    ...mapGetters({
      volumeTokenCreated: 'volumetoken/create/created',
      volumeTokenLoading: 'volumetoken/create/isLoading',
      volumeTokenCreateError: 'volumetoken/create/error',
      volumeTokenViolations: 'volumetoken/create/violations',
      volumeTokenDeleted: 'volumetoken/del/deleted',
      volumeTokenDeleteError: 'volumetoken/del/error',
      volumeTokenDeleteLoading: 'volumetoken/del/isLoading',
      paymentCreated: 'payment/create/created',
      paymentLoading: 'payment/create/isLoading',
      paymentCreateError: 'payment/create/error',
      paymentViolations: 'payment/create/violations'
    }),
    dialog: {
      get: function() {
        return this.show
      },
      set: function(newValue) {
        if (newValue === false) {
          this.$emit('close')
        }
        return this.show
      }
    },

    grossPrice() {
      return (
        this.selectedVolume.price + (this.selectedVolume.price / 100) * this.servicePeriod.agreement.legalEntity.vat
      )
    },

    stripeData() {
      const legalEntity = this.servicePeriod.agreement.legalEntity
      if (this.cardHolderName) {
        let data = {
          payment_method_data: {
            billing_details: {
              name: this.cardHolderName,
              email: legalEntity.email,
              address: {
                city: legalEntity.city,
                country: legalEntity.country,
                line1: legalEntity.address,
                state: legalEntity.region
              },
              phone: legalEntity.phone
            }
          },
          receipt_email: legalEntity.email
        }

        if (legalEntity.postalCode) {
          data.payment_method_data.billing_details.address.postal_code = legalEntity.postalCode
        }

        return data
      }

      return null
    }
  },

  watch: {
    selectedVolume(selectedVolume) {
      this.volumeToken.volume = selectedVolume.volume
    },

    volumeTokenCreated: function(volumeTokenCreated) {
      if (!volumeTokenCreated) {
        return
      }

      let payment = {}
      payment.status = 'pending'
      payment.type = 'credit_card_online'
      payment.parentOrder = volumeTokenCreated.orderLine.parentOrder['@id']
      this.createPayment(payment)
    },

    paymentCreated: function(paymentCreated) {
      if (!paymentCreated) {
        return
      }
      this.$refs.cardElement.handleCardPayment(paymentCreated.clientSecret)
    },

    paymentCreateError(paymentCreateError) {
      if (!paymentCreateError) {
        return
      }

      // delete volume token
    }
  },

  methods: {
    ...mapActions({
      createVolumeToken: 'volumetoken/create/create',
      resetVolumeToken: 'volumetoken/create/reset',
      deleteVolumeToken: 'volumetoken/del/del',
      delVolumeTokenReset: 'order/del/reset',
      createPayment: 'payment/create/create',
      resetPayment: 'payment/create/reset'
    }),

    cardPaymentSuccess() {
      const _this = this
      let attempts = 10
      let attempt = 1
      this.checkingDone = true
      this.paymentDoneCheckInternval = setInterval(() => {
        fetch(_this.paymentCreated ? _this.paymentCreated['@id'] : _this.relevantPayment['@id'])
          .then(response => response.json())
          .then(data => {
            if (data.status === 'done') {
              clearInterval(this.paymentDoneCheckInternval)
              _this.$emit('paymentCreated', this.paymentCreated)
              _this.resetPayment()
              _this.resetVolumeToken()
              _this.delVolumeTokenReset()
              _this.checkingDone = false
              _this.dialog = false
            }
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.log(e)
            this.checkingDone = false
            this.deleteVolumeToken(this.volumeTokenCreated)
          })
          .then(() => {
            if (attempt >= attempts) {
              this.checkingDone = false
              clearInterval(this.paymentDoneCheckInternval)
              this.deleteVolumeToken(this.volumeTokenCreated)
              _this.cardError = 'Your payment was success but your service can not be activated. Please contact us.'
            }
            attempt = attempt + 1
          })
      }, 3000)
    },

    cardPaymentError(error) {
      this.cardError = error.message
      this.$emit('cardError')
    },

    setStripeLoading(value) {
      this.stripeLoading = value
    },

    onSubmit() {
      if (this.$refs.form.validate()) {
        this.createVolumeToken(this.volumeToken)
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.card-error
    >>>.fake-material-input
        background rgba(255, 0, 0, 0.1)

        .input-effect
            background-color #E15141

.stripe-card
    max-width 400px
    width 100%

.stripe-card.complete
    border-color green
</style>
